/**
 * The Command Desk's **Cap Milestones** panel, and the dated watch it generates — RUN 97.
 *
 * Authority: the Technical Delegate, 27 September 2026 — *"I will like to add this milestone
 * report as a standard function in our TD Control Centre (Maybe a small additional section to
 * show a few highlights and then a button to generate an updated report (dated on the
 * report))."* Routes: `GET /caps` and `POST /caps/refresh` (`requireTd`). The arithmetic, and
 * what it refuses to do, is `milestones.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS A RAIL PANEL ON THE DESK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * He asked for "a small additional section", and a module of its own would be a ninth page for
 * three lines of highlights. The Desk's rail is where tournament-wide colour already lives — the
 * cards averages, pool progress — and a player's 100th cap is a thing the TD has to ACT on (a
 * presentation arranged, a word with the team manager) on the day it happens, which is the
 * rail's "what must I chase" question. It sits after UM Activity, the last section, so no panel
 * §9 orders moves. Like UM Activity it reads its own route: its figures come from TMS, not from
 * another module's data.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT SHOWS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   * **When the caps were read** from TMS, and — if the last press did not reach every team —
 *     which teams it missed and why. A panel that said "last refreshed 10:31" over four teams'
 *     caps from yesterday would be the stale snapshot again.
 *   * **A match played since the caps were read**, per team: those caps do not include it, and
 *     the panel says "refresh" in words rather than letting a next-match milestone quietly become
 *     last match's.
 *   * **The highlights**: players whose NEXT appearance is a milestone; milestones reached here
 *     in the last few days, flagged `Reached — Recognition?`; and how many players are within
 *     three appearances. **Compact since the same afternoon** (the TD: "far too tall"): three
 *     one-line rows by default (`milestoneHighlights` holds the rule and argues it), the rest
 *     behind the house fold (`TipsFold`) whose summary row carries the counts.
 *   * **`Refresh & Generate`**: the server reads every team page from TMS now (and only now —
 *     nothing reads them on a schedule), then the full dated watch opens as a sheet, and `Print /
 *     Save As PDF` puts it on one page named `Cap-Milestones-Watch-<date>` (`capSheetPrint.ts`).
 *     A press that read no team opens no sheet: there is nothing new to date.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { fetchCaps, postCapsRefresh } from "../../api/client.js";
import type { CapsBody, CapsRefreshBody, CapsRefreshRow, FixtureRowView } from "../../api/types.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { tournamentDay, tournamentTimeOfDay } from "../../app/tournamentTime.js";
import { TipsFold } from "../appointments/TipsFold.js";
import { printCapSheet } from "./capSheetPrint.js";
import { dayMonth } from "./derive.js";
import {
  WATCH_WINDOW,
  capWatch,
  competitionSection,
  milestoneHighlights,
  ordinalCap,
  recentlyReached,
  type CapWatch,
  type Highlight,
  type MilestoneHighlights,
  type UpcomingMilestone,
} from "./milestones.js";

/**
 * `27 Sep · 10:31` on the tournament clock. Built from the Desk's own month names (`dayMonth`)
 * rather than `Intl`'s, whose short month for September is `Sept` in some engines and `Sep` in
 * others: a dated sheet must read the same on every laptop it is printed from.
 */
export function readStamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${dayMonth(tournamentDay(at))} · ${tournamentTimeOfDay(at)}`;
}

/** `27 Sep 2026` on the tournament clock, on the same terms. */
function readDateLong(at: Date): string {
  const day = tournamentDay(at);
  return `${dayMonth(day)} ${day.slice(0, 4)}`;
}

/** `IND (HTTP 503)`, `PAK (not read)` — the teams a press missed, short enough for the rail. */
export function missedTeamsLine(refresh: CapsRefreshRow): string {
  return refresh.teamsFailed
    .map(
      (failure) =>
        `${failure.competitionId === "1917" ? "W " : ""}${failure.teamCode} (${
          failure.httpStatus === null ? "not read" : `HTTP ${String(failure.httpStatus)}`
        })`,
    )
    .join(", ");
}

/** The stamp line, in the TD's words. */
export function readLine(latest: CapsRefreshRow | null): string {
  if (latest === null) return "Caps Have Not Been Read From TMS Yet";
  const when = readStamp(latest.finishedAt);
  if (latest.outcome === "complete") return `Caps Read Live From TMS · ${when}`;
  if (latest.outcome === "partial") return `Caps Read Live From TMS · ${when} · ${String(latest.teamsFailed.length)} Teams Not Read`;
  return `Last Refresh ${when} Read No Team — Caps Held Are Unchanged`;
}

/** `1 player’s caps have` / `3 players’ caps have` never been read from TMS. */
function unreadLine(count: number): string {
  return `${String(count)} ${count === 1 ? "player\u2019s" : "players\u2019"} caps have never been read from TMS`;
}

function rowKey(row: { competitionId: string; teamCode: string; shirtNumber: number }): string {
  return `${row.competitionId}:${row.teamCode}:${String(row.shirtNumber)}`;
}

/**
 * The fold's summary row: what is behind it, counted, so nothing on the panel is invisible
 * while it is shut — `5 more next match · 4 more reached · 12 within 3`. The within-three count
 * is always there, so even a day whose rows all fit still says how many the watch holds.
 */
export function foldSummary(highlights: MilestoneHighlights, withinWindow: number): string {
  const parts: string[] = [];
  if (highlights.moreNext.length > 0) parts.push(`${String(highlights.moreNext.length)} more next match`);
  if (highlights.moreReached.length > 0) parts.push(`${String(highlights.moreReached.length)} more reached`);
  parts.push(`${String(withinWindow)} within ${String(WATCH_WINDOW)}`);
  return parts.join(" · ");
}

/**
 * One compact line: `LEE Yuri · 150th · 28 Sep`. The name, the milestone and the day are what
 * the TD reads at a glance; shirt, team and the reasoning behind the date live in the row's
 * `title`, and on the dated watch. A recognition carries a `Reached` tag in front, because with
 * the section heads folded away the row has to say which kind it is by itself. `~` (an undrawn
 * fixture's date), `by` (a day he may have been rested) and `if selected` stay on the line: each
 * changes what the date means, and a tooltip is not read at a glance.
 */
function HighlightRow({ item }: { item: Highlight }): React.JSX.Element {
  const who = `#${String(item.row.shirtNumber)} ${teamLabel(item.row)} ${item.row.teamName}`;
  if (item.kind === "next") {
    const row = item.row;
    const date = `${row.approximate ? "~" : ""}${dayMonth(row.likelyDate)}`;
    return (
      <li
        className="capms__row"
        title={`${row.displayName} · ${who} · ${String(row.capsNow)} caps now · ${ordinalCap(row.milestone)} cap at his next appearance, likely ${date}${row.ifSelected ? " (if selected)" : ""}`}
      >
        <b className="capms__who">{row.displayName}</b>
        <span className="capms__meta">
          {" "}
          · {ordinalCap(row.milestone)} · {date}
          {row.ifSelected ? " · if selected" : ""}
        </span>
      </li>
    );
  }
  const row = item.row;
  const date = row.reachedBy === null ? "" : `${row.exact ? "" : "by "}${dayMonth(row.reachedBy)}`;
  return (
    <li
      className="capms__row capms__row--reached"
      title={`${row.displayName} · ${who} · ${ordinalCap(row.milestone)} cap reached at these Games${date === "" ? "" : `, ${date}`} — recognition?`}
    >
      <span className="capms__tag">Reached</span>
      <b className="capms__who">{row.displayName}</b>
      <span className="capms__meta">
        {" "}
        · {ordinalCap(row.milestone)}
        {date === "" ? "" : ` · ${date}`}
      </span>
    </li>
  );
}

function teamLabel(row: { competitionId: string; teamCode: string }): string {
  return `${row.competitionId === "1917" ? "W " : ""}${row.teamCode}`;
}

export function CapMilestonesPanel({
  fixtures,
  now,
}: {
  /** The Desk's schedule-wide fixture read — the one `matchCompleted` already judges. */
  fixtures: readonly FixtureRowView[];
  /** The Desk's minute. */
  now: Date;
}): React.JSX.Element {
  const [body, setBody] = useState<CapsBody | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [pressing, setPressing] = useState(false);
  const [pressError, setPressError] = useState<Error | null>(null);
  const [press, setPress] = useState<CapsRefreshBody | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let live = true;
    fetchCaps().then(
      (answer) => {
        if (live) setBody(answer);
      },
      (error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error : new Error(String(error)));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const watch = useMemo<CapWatch | null>(
    () => (body === null ? null : capWatch({ players: body.players, fixtures, now })),
    [body, fixtures, now],
  );

  async function refreshAndGenerate(): Promise<void> {
    setPressing(true);
    setPressError(null);
    try {
      const answer = await postCapsRefresh();
      setBody(answer);
      setPress(answer);
      if (answer.refresh.teamsRead > 0) setSheetOpen(true);
    } catch (error) {
      setPressError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setPressing(false);
    }
  }

  const latest = body?.latestRefresh ?? null;
  const today = tournamentDay(now);
  const highlights =
    watch === null
      ? null
      : milestoneHighlights(
          watch.upcoming.filter((row) => row.matchesAway === 1),
          recentlyReached(watch, today),
          today,
        );
  const stale = watch?.teams.filter((team) => team.playedSinceRead > 0) ?? [];

  return (
    <section className="panel panel--d4 capms" aria-labelledby="capMilestonesTitle" data-testid="cap-milestones">
      <div className="panel__head">
        <h3 className="panel__title" id="capMilestonesTitle">
          Cap Milestones
        </h3>
        <span className="panel__note">Every 50th Cap · Live TMS Caps</span>
      </div>

      {body === null && loadError === null ? <Skeleton rows={3} /> : null}
      {loadError !== null ? <FailBanner error={loadError} role="status" /> : null}

      {body !== null && watch !== null ? (
        <div className="capms__body">
          <p className={`capms__read${latest?.outcome === "complete" ? "" : " capms__read--warn"}`} data-testid="capms-read">
            {readLine(latest)}
          </p>
          {latest !== null && latest.outcome !== "complete" && latest.teamsFailed.length > 0 ? (
            <p
              className="capms__warn capms__warn--line"
              data-testid="capms-missed"
              title="These teams were not read at the last refresh: their caps are from an earlier read, or there are none."
            >
              Not read: {missedTeamsLine(latest)} — older caps held.
            </p>
          ) : null}
          {stale.length > 0 ? (
            <p
              className="capms__warn capms__warn--line"
              data-testid="capms-stale"
              title="A match has been played since these caps were read, so they do not include it. Press Refresh & Generate before relying on them."
            >
              Played since read: {stale.map(teamLabel).join(", ")} — refresh before relying on these.
            </p>
          ) : null}

          {latest === null || highlights === null ? null : (
            <>
              {highlights.shown.length === 0 ? (
                <p className="capms__none">Nobody is one appearance from a milestone.</p>
              ) : (
                <ul className="capms__rows" data-testid="capms-top">
                  {highlights.shown.map((item) => (
                    <HighlightRow key={`${item.kind}:${rowKey(item.row)}`} item={item} />
                  ))}
                </ul>
              )}

              <TipsFold
                storageKey="hockeytd.fold.cap-milestones"
                testId="capms-more"
                className="capms__fold"
                summary={foldSummary(highlights, watch.upcoming.length)}
                cue={{ shut: "show", open: "hide" }}
              >
                {highlights.moreNext.length === 0 ? null : (
                  <>
                    <h4 className="capms__h">Next Match Brings A Milestone</h4>
                    <ul className="capms__rows" data-testid="capms-next">
                      {highlights.moreNext.map((row) => (
                        <HighlightRow key={rowKey(row)} item={{ kind: "next", row }} />
                      ))}
                    </ul>
                  </>
                )}
                {highlights.moreReached.length === 0 ? null : (
                  <>
                    <h4 className="capms__h capms__h--reached">Reached — Recognition?</h4>
                    <ul className="capms__rows" data-testid="capms-reached">
                      {highlights.moreReached.map((row) => (
                        <HighlightRow key={rowKey(row)} item={{ kind: "reached", row }} />
                      ))}
                    </ul>
                  </>
                )}
                <p className="capms__count" data-testid="capms-count">
                  <b className="num">{watch.upcoming.length}</b>{" "}
                  {watch.upcoming.length === 1 ? "player is" : "players are"} within {WATCH_WINDOW} appearances of a
                  milestone — every one is on the dated watch.
                </p>
              </TipsFold>
            </>
          )}
          {watch.unread > 0 && latest !== null ? (
            <p className="capms__foot" data-testid="capms-unread">
              {unreadLine(watch.unread)} — not counted.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="capms__actions">
        <button
          type="button"
          className="capms__press"
          onClick={() => void refreshAndGenerate()}
          disabled={pressing || body?.refreshing === true}
          data-testid="capms-press"
        >
          {pressing ? "Reading TMS…" : "Refresh & Generate"}
        </button>
        {press !== null && press.refresh.teamsRead > 0 && !sheetOpen ? (
          <button type="button" className="capms__reopen" onClick={() => setSheetOpen(true)}>
            Open Watch
          </button>
        ) : null}
      </div>
      {pressError !== null ? (
        <FailBanner
          error={
            new Error(
              `No answer came back from the refresh (${pressError.message}). Nothing on this panel has ` +
                "changed; reload the Desk to see whether the refresh ran.",
            )
          }
        />
      ) : null}
      {press !== null && press.refresh.teamsRead === 0 ? (
        <p className="capms__warn" role="status" data-testid="capms-press-failed">
          The refresh read no team from TMS
          {press.refresh.teamsFailed[0] === undefined ? "" : ` (${press.refresh.teamsFailed[0].reason})`}. The caps held
          are unchanged and no watch was generated.
        </p>
      ) : null}

      {sheetOpen && press !== null && watch !== null
        ? createPortal(
            <CapWatchSheet press={press} watch={watch} onClose={() => setSheetOpen(false)} />,
            document.body,
          )
        : null}
    </section>
  );
}

/* ── The dated watch ─────────────────────────────────────────────────────── */

function WatchTable({ rows }: { rows: UpcomingMilestone[] }): React.JSX.Element {
  return (
    <table className="capsheet__table">
      <thead>
        <tr>
          <th scope="col">Shirt</th>
          <th scope="col">Team</th>
          <th scope="col">Code</th>
          <th scope="col">Player</th>
          <th scope="col" className="num">Caps Now</th>
          <th scope="col" className="num">Milestone</th>
          <th scope="col">Likely Date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.competitionId}:${row.teamCode}:${String(row.shirtNumber)}`}>
            <td className="num">{row.shirtNumber}</td>
            <td>{row.teamName}</td>
            <td>{row.teamCode}</td>
            <td>
              {row.displayName}
              {row.ifSelected ? <span className="capsheet__ifsel"> (if selected)</span> : null}
            </td>
            <td className="num">{row.capsNow}</td>
            <td className="num">{row.milestone}</td>
            <td>
              {row.approximate ? "~" : ""}
              {dayMonth(row.likelyDate)}
              <span className="capsheet__away"> · {row.matchesAway === 1 ? "next match" : `${String(row.matchesAway)} matches`}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CapWatchSheet({
  press,
  watch,
  onClose,
}: {
  press: CapsRefreshBody;
  watch: CapWatch;
  onClose: () => void;
}): React.JSX.Element {
  const sheet = useRef<HTMLElement>(null);
  const generated = new Date(press.refresh.finishedAt);
  const day = tournamentDay(generated);
  const sections = (["1916", "1917"] as const).map((competitionId) => ({
    competitionId,
    title: competitionSection(competitionId),
    rows: watch.upcoming.filter((row) => row.competitionId === competitionId),
    reached: watch.reached.filter((row) => row.competitionId === competitionId),
  }));
  const pressStarted = press.refresh.startedAt;
  // Teams whose caps on this sheet are NOT from this press - an older read, stated per team.
  const older = watch.teams.filter((team) => team.readAt !== null && team.readAt < pressStarted);
  const inPlay = watch.teams.filter((team) => team.inPlayAtRead);

  return (
    <div className="capsheet-overlay" role="dialog" aria-modal="true" aria-labelledby="capSheetTitle">
      <div className="capsheet-controls">
        <button type="button" className="capms__press" onClick={() => printCapSheet(sheet.current, day)} data-testid="capsheet-print">
          Print / Save As PDF
        </button>
        <button type="button" className="capms__reopen" onClick={onClose}>
          Close
        </button>
      </div>
      <article className="capsheet" ref={sheet} data-testid="capsheet">
        <header className="capsheet__head">
          <h2 id="capSheetTitle">Cap Milestones Watch</h2>
          <p className="capsheet__dated" data-testid="capsheet-dated">
            Generated {readDateLong(generated)} · {tournamentTimeOfDay(generated)} (Tokyo) · Caps read live from
            TMS at this refresh · Players within {WATCH_WINDOW} appearances of their next 50th cap, soonest first
          </p>
        </header>

        {sections.map((section) => (
          <section key={section.competitionId} className="capsheet__section" data-testid={`capsheet-${section.competitionId}`}>
            <h3>{section.title} Competition</h3>
            {section.rows.length === 0 ? (
              <p className="capsheet__none">No player is within {WATCH_WINDOW} appearances of a milestone.</p>
            ) : (
              <WatchTable rows={section.rows} />
            )}
            {section.reached.length === 0 ? null : (
              <p className="capsheet__reached">
                <b>Reached at these Games — recognition?</b>{" "}
                {section.reached
                  .map(
                    (row) =>
                      `${row.displayName} (${row.teamCode} #${String(row.shirtNumber)}) ${ordinalCap(row.milestone)}${
                        row.reachedBy === null ? "" : `, ${row.exact ? "" : "by "}${dayMonth(row.reachedBy)}`
                      }`,
                  )
                  .join("; ")}
              </p>
            )}
          </section>
        ))}

        <footer className="capsheet__notes" data-testid="capsheet-notes">
          <ol>
            <li>
              <b>Caps now</b> is TMS&rsquo;s <i>Current Caps</i> exactly as read from each team&rsquo;s public TMS page at the
              refresh of {readStamp(press.refresh.startedAt)} (Tokyo). Nothing is added to TMS&rsquo;s figure.
              {press.refresh.teamsFailed.length === 0
                ? " Every team was read at this refresh."
                : ` Not read at this refresh: ${missedTeamsLine(press.refresh)}.`}
              {older.length === 0
                ? ""
                : ` Caps shown for ${older.map((team) => `${teamLabel(team)} (read ${readStamp(team.readAt ?? "")})`).join(", ")} are from that earlier read.`}
            </li>
            <li>
              A milestone is every 50th cap. A player exactly at a milestone has reached it; his next is the one after.
            </li>
            <li>
              <b>Likely date</b> is his team&rsquo;s Nth remaining fixture in schedule order, N being the caps still
              needed. <b>~</b> marks a date taken from a knockout fixture whose teams are not yet drawn. A milestone beyond
              the team&rsquo;s remaining fixtures is not listed.
            </li>
            <li>
              <b>(if selected)</b>: the player has played fewer than half his team&rsquo;s matches at these Games, so the
              date assumes a selection he has not been having.
            </li>
            {inPlay.length === 0 ? null : (
              <li>
                {inPlay.map(teamLabel).join(", ")} {inPlay.length === 1 ? "was" : "were"} playing when the caps were read;
                that match may or may not be in TMS&rsquo;s count.
              </li>
            )}
            {press.refresh.unmatched.length === 0 ? null : (
              <li>
                Not on this sheet — TMS rows that could not be matched to the team list held here:{" "}
                {press.refresh.unmatched
                  .map((row) => `${teamLabel(row)} #${String(row.shirtNumber)} ${row.name}`)
                  .join("; ")}
                .
              </li>
            )}
            {watch.unread === 0 ? null : (
              <li>{unreadLine(watch.unread)} and are not on this sheet.</li>
            )}
          </ol>
        </footer>
      </article>
    </div>
  );
}
